import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  Star,
  MapPin,
  Phone,
  Globe,
  Clock,
  Share2,
  Heart,
  CheckCircle,
  Navigation,
  Mail,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Wifi,
  CreditCard,
  Car,
  ParkingCircle,
  X,
  ChevronRight,
  Tag,
  Camera,
  MessageSquare,
  Send,
  ThumbsUp,
  User,
  Loader2,
  Edit3,
  Trash2
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const BusinessDetailPage = ({ business, onBack, onTabChange }) => {
  const { darkMode } = useTheme();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLiked, setIsLiked] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Review states
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewStats, setReviewStats] = useState({ total: 0, average_rating: 0 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [editingReview, setEditingReview] = useState(false);
  
  // Claim states
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimMessage, setClaimMessage] = useState('');
  const [submittingClaim, setSubmittingClaim] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f8fafc]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  // Business data with defaults
  const data = {
    id: business?.id || '',
    name: business?.name || 'İşletme Adı',
    description: business?.description || '',
    short_description: business?.short_description || 'Kaliteli hizmet anlayışı',
    image: business?.image || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800',
    gallery: business?.gallery || [],
    category: business?.category || 'restaurant',
    category_name: business?.category_name || 'Restoran',
    rating: business?.rating || 4.5,
    reviewCount: business?.reviewCount || business?.review_count || 0,
    priceRange: business?.price_range || '$$',
    address: business?.address || 'Giresun Merkez',
    phone: business?.phone || '',
    website: business?.website || '',
    email: business?.email || '',
    isOpen: business?.isOpen ?? true,
    isVerified: business?.is_verified ?? false,
    workingHours: business?.working_hours || {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '10:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '21:00', closed: false },
    },
    features: business?.features || ['wifi', 'credit_card', 'parking'],
    tags: business?.tags || [],
    socialMedia: business?.social_media || {},
    maps_url: business?.maps_url || '',
    latitude: business?.latitude || null,
    longitude: business?.longitude || null,
    campaigns: business?.campaigns || [],
    owner_id: business?.owner_id || null,
  };

  const dayNames = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };

  const featureIcons = {
    wifi: { icon: Wifi, label: 'Kablosuz İnternet' },
    credit_card: { icon: CreditCard, label: 'Kredi Kartı' },
    parking: { icon: ParkingCircle, label: 'Otopark' },
    wheelchair: { icon: CheckCircle, label: 'Engelli Erişimi' },
  };

  const tabs = [
    { id: 'profile', label: 'Profil' },
    { id: 'reviews', label: `Yorumlar (${reviewStats.total || data.reviewCount})` },
    { id: 'gallery', label: `Galeri (${data.gallery.length})` },
  ];

  // Fetch reviews when tab changes or component mounts
  useEffect(() => {
    if (data.id) {
      fetchReviews();
    }
  }, [data.id]);

  const fetchReviews = async () => {
    if (!data.id) return;
    
    setReviewsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews/business/${data.id}`);
      if (response.ok) {
        const result = await response.json();
        setReviews(result.reviews || []);
        setReviewStats({
          total: result.total || 0,
          average_rating: result.average_rating || 0
        });
        
        // Check if current user has already reviewed
        if (user) {
          const existingReview = result.reviews?.find(r => r.user_id === user.id);
          setUserReview(existingReview || null);
        }
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user || !token) {
      alert('Yorum yapmak için giriş yapmalısınız');
      return;
    }

    if (!reviewForm.comment.trim()) {
      alert('Lütfen bir yorum yazın');
      return;
    }

    setSubmittingReview(true);
    try {
      const url = editingReview 
        ? `${API_URL}/api/reviews/${userReview.id}`
        : `${API_URL}/api/reviews`;
      
      const method = editingReview ? 'PUT' : 'POST';
      
      const body = editingReview 
        ? { rating: reviewForm.rating, comment: reviewForm.comment }
        : { business_id: data.id, rating: reviewForm.rating, comment: reviewForm.comment };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setShowReviewForm(false);
        setEditingReview(false);
        setReviewForm({ rating: 5, comment: '' });
        fetchReviews();
      } else {
        const error = await response.json();
        alert(error.detail || 'Bir hata oluştu');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Yorum gönderilirken bir hata oluştu');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setUserReview(null);
        fetchReviews();
      }
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const handleMarkHelpful = async (reviewId) => {
    if (!token) {
      alert('Bu özellik için giriş yapmalısınız');
      return;
    }

    try {
      await fetch(`${API_URL}/api/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchReviews();
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  const handleEditReview = () => {
    if (userReview) {
      setReviewForm({
        rating: userReview.rating,
        comment: userReview.comment
      });
      setEditingReview(true);
      setShowReviewForm(true);
    }
  };

  const handleGetDirections = () => {
    if (data.maps_url) {
      window.open(data.maps_url, '_blank');
    } else if (data.latitude && data.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${data.latitude},${data.longitude}`, '_blank');
    }
  };

  // Claim business
  const handleSubmitClaim = async () => {
    if (!user || !token) {
      alert('İşletme talep etmek için giriş yapmalısınız');
      return;
    }

    if (!claimMessage.trim()) {
      alert('Lütfen neden bu işletmenin sahibi olduğunuzu açıklayın');
      return;
    }

    setSubmittingClaim(true);
    try {
      const response = await fetch(`${API_URL}/api/business-claims?business_id=${data.id}&message=${encodeURIComponent(claimMessage)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Talebiniz gönderildi! Admin onayı bekleniyor.');
        setShowClaimModal(false);
        setClaimMessage('');
      } else {
        const error = await response.json();
        alert(error.detail || 'Talep gönderilemedi');
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      alert('Bir hata oluştu');
    } finally {
      setSubmittingClaim(false);
    }
  };

  // Star Rating Component
  const StarRating = ({ rating, onChange, size = 'md', interactive = false }) => {
    const sizeClass = size === 'lg' ? 'w-8 h-8' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onChange && onChange(star)}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star
              className={`${sizeClass} ${
                star <= rating 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : darkMode ? 'text-slate-600' : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Review Card Component
  const ReviewCard = ({ review }) => {
    const isOwnReview = user && review.user_id === user.id;
    const reviewDate = new Date(review.created_at).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return (
      <div className={`${bgCard} rounded-2xl p-4 shadow-sm`}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} flex items-center justify-center`}>
            {review.user_avatar ? (
              <img src={review.user_avatar} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className={`w-5 h-5 ${textSecondary}`} />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h4 className={`font-medium ${textPrimary}`}>{review.user_name}</h4>
                <p className={`text-xs ${textSecondary}`}>{reviewDate}</p>
              </div>
              <StarRating rating={review.rating} size="sm" />
            </div>
            <p className={`mt-2 text-sm ${textPrimary}`}>{review.comment}</p>
            
            {/* Reply from business */}
            {review.reply && (
              <div className={`mt-3 p-3 rounded-xl ${darkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
                <p className={`text-xs font-medium ${textSecondary} mb-1`}>İşletme Yanıtı:</p>
                <p className={`text-sm ${textPrimary}`}>{review.reply}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-4 mt-3">
              <button
                onClick={() => handleMarkHelpful(review.id)}
                className={`flex items-center gap-1 text-xs ${textSecondary} hover:text-emerald-500 transition-colors`}
              >
                <ThumbsUp className="w-4 h-4" />
                <span>Faydalı ({review.helpful_count || 0})</span>
              </button>
              
              {isOwnReview && (
                <>
                  <button
                    onClick={handleEditReview}
                    className={`flex items-center gap-1 text-xs ${textSecondary} hover:text-blue-500 transition-colors`}
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Düzenle</span>
                  </button>
                  <button
                    onClick={() => handleDeleteReview(review.id)}
                    className={`flex items-center gap-1 text-xs ${textSecondary} hover:text-red-500 transition-colors`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Sil</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${bgMain} pb-24`}>
      <div className="max-w-3xl mx-auto">
      {/* Hero Image */}
      <div className="relative h-80">
        <img
          src={data.image}
          alt={data.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <div className="flex gap-2">
            <button className="w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Share2 className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => setIsLiked(!isLiked)}
              className="w-10 h-10 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center"
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-white'}`} />
            </button>
          </div>
        </div>

        {/* Business Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 mb-1">
            {data.isVerified && (
              <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Onaylı
              </span>
            )}
            <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm text-white text-xs rounded-full">
              {data.category_name}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">{data.name}</h1>
          <div className="flex items-center gap-3 text-white/90 text-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold">{reviewStats.average_rating || data.rating}</span>
              <span className="text-white/70">({reviewStats.total || data.reviewCount} yorum)</span>
            </div>
            <span>•</span>
            <span>{data.priceRange}</span>
            <span>•</span>
            <span className={data.isOpen ? 'text-emerald-400' : 'text-red-400'}>
              {data.isOpen ? 'Açık' : 'Kapalı'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`sticky top-0 z-10 ${bgCard} border-b ${borderColor}`}>
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-emerald-500 border-b-2 border-emerald-500'
                  : textSecondary
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'profile' && (
          <div className="space-y-4">
            {/* Description */}
            {data.description && (
              <div className={`${bgCard} rounded-2xl p-4 shadow-sm`}>
                <h3 className={`font-semibold ${textPrimary} mb-2`}>Hakkında</h3>
                <p className={`text-sm ${textSecondary} leading-relaxed`}>{data.description}</p>
              </div>
            )}

            {/* Call Now - Main Action Button */}
            {data.phone && (
              <a
                href={`tel:${data.phone}`}
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-3 shadow-lg transition-all duration-200 active:scale-[0.98]"
              >
                <Phone className="w-5 h-5" />
                <span>Hemen Ara</span>
              </a>
            )}

            {/* Claim Business Button - Only show if no owner */}
            {user && !data.owner_id && user.role !== 'business_owner' && (
              <button
                onClick={() => setShowClaimModal(true)}
                className={`w-full py-3 ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary} rounded-2xl font-medium flex items-center justify-center gap-2 transition-colors`}
              >
                <CheckCircle className="w-5 h-5 text-blue-500" />
                Bu İşletmeyi Talep Et
              </button>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-4 gap-2">
              {data.website && (
                <a
                  href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${bgCard} rounded-xl p-3 flex flex-col items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow`}
                >
                  <Globe className="w-5 h-5 text-blue-500" />
                  <span className={`text-xs ${textSecondary}`}>Website</span>
                </a>
              )}
              {(data.maps_url || (data.latitude && data.longitude)) && (
                <button
                  onClick={handleGetDirections}
                  className={`${bgCard} rounded-xl p-3 flex flex-col items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow`}
                >
                  <Navigation className="w-5 h-5 text-orange-500" />
                  <span className={`text-xs ${textSecondary}`}>Yol Tarifi</span>
                </button>
              )}
              <button
                onClick={() => setIsLiked(!isLiked)}
                className={`${bgCard} rounded-xl p-3 flex flex-col items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow`}
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-red-400'}`} />
                <span className={`text-xs ${textSecondary}`}>Kaydet</span>
              </button>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: data.name,
                      text: data.short_description,
                      url: window.location.href
                    });
                  }
                }}
                className={`${bgCard} rounded-xl p-3 flex flex-col items-center gap-1.5 shadow-sm hover:shadow-md transition-shadow`}
              >
                <Share2 className="w-5 h-5 text-purple-500" />
                <span className={`text-xs ${textSecondary}`}>Paylaş</span>
              </button>
            </div>

            {/* Contact Info */}
            <div className={`${bgCard} rounded-2xl p-4 shadow-sm space-y-3`}>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>İletişim Bilgileri</h3>
              
              {data.address && (
                <div className="flex items-start gap-3">
                  <MapPin className={`w-5 h-5 ${textSecondary} mt-0.5`} />
                  <span className={`text-sm ${textPrimary}`}>{data.address}</span>
                </div>
              )}
              
              {data.phone && (
                <div className="flex items-center gap-3">
                  <Phone className={`w-5 h-5 ${textSecondary}`} />
                  <a href={`tel:${data.phone}`} className={`text-sm ${textPrimary}`}>{data.phone}</a>
                </div>
              )}
              
              {data.email && (
                <div className="flex items-center gap-3">
                  <Mail className={`w-5 h-5 ${textSecondary}`} />
                  <a href={`mailto:${data.email}`} className={`text-sm ${textPrimary}`}>{data.email}</a>
                </div>
              )}
            </div>

            {/* Working Hours */}
            {data.workingHours && (
              <div className={`${bgCard} rounded-2xl p-4 shadow-sm`}>
                <h3 className={`font-semibold ${textPrimary} mb-3 flex items-center gap-2`}>
                  <Clock className="w-5 h-5 text-emerald-500" />
                  Çalışma Saatleri
                </h3>
                <div className="space-y-2">
                  {Object.entries(data.workingHours).map(([day, hours]) => (
                    <div key={day} className="flex justify-between text-sm">
                      <span className={textSecondary}>{dayNames[day]}</span>
                      <span className={textPrimary}>
                        {hours.closed ? 'Kapalı' : `${hours.open} - ${hours.close}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            {data.features && data.features.length > 0 && (
              <div className={`${bgCard} rounded-2xl p-4 shadow-sm`}>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Özellikler</h3>
                <div className="flex flex-wrap gap-2">
                  {data.features.map((feature) => {
                    const FeatureIcon = featureIcons[feature]?.icon || Tag;
                    const label = featureIcons[feature]?.label || feature;
                    return (
                      <span
                        key={feature}
                        className={`px-3 py-1.5 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full text-sm ${textPrimary} flex items-center gap-2`}
                      >
                        <FeatureIcon className="w-4 h-4 text-emerald-500" />
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Social Media */}
            {data.socialMedia && Object.keys(data.socialMedia).length > 0 && (
              <div className={`${bgCard} rounded-2xl p-4 shadow-sm`}>
                <h3 className={`font-semibold ${textPrimary} mb-3`}>Sosyal Medya</h3>
                <div className="flex gap-3">
                  {data.socialMedia.facebook && (
                    <a href={data.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <Facebook className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {data.socialMedia.instagram && (
                    <a href={data.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 rounded-full flex items-center justify-center">
                      <Instagram className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {data.socialMedia.twitter && (
                    <a href={data.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-sky-500 rounded-full flex items-center justify-center">
                      <Twitter className="w-5 h-5 text-white" />
                    </a>
                  )}
                  {data.socialMedia.youtube && (
                    <a href={data.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                      <Youtube className="w-5 h-5 text-white" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {/* Rating Summary */}
            <div className={`${bgCard} rounded-2xl p-4 shadow-sm`}>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className={`text-4xl font-bold ${textPrimary}`}>
                    {reviewStats.average_rating || data.rating}
                  </div>
                  <StarRating rating={Math.round(reviewStats.average_rating || data.rating)} size="sm" />
                  <p className={`text-xs ${textSecondary} mt-1`}>{reviewStats.total} yorum</p>
                </div>
                <div className="flex-1">
                  {/* Rating distribution bars could go here */}
                </div>
              </div>
            </div>

            {/* Write Review Button */}
            {!userReview && user && (
              <button
                onClick={() => setShowReviewForm(true)}
                className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                Yorum Yaz
              </button>
            )}

            {!user && (
              <div className={`${bgCard} rounded-2xl p-4 text-center`}>
                <p className={`text-sm ${textSecondary}`}>Yorum yapmak için giriş yapmalısınız</p>
              </div>
            )}

            {/* Review Form Modal */}
            {showReviewForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
                <div className={`${bgCard} rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto`}>
                  <div className={`p-4 border-b ${borderColor} flex items-center justify-between`}>
                    <h3 className={`font-semibold ${textPrimary}`}>
                      {editingReview ? 'Yorumu Düzenle' : 'Yorum Yaz'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowReviewForm(false);
                        setEditingReview(false);
                        setReviewForm({ rating: 5, comment: '' });
                      }}
                      className={`w-8 h-8 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} flex items-center justify-center`}
                    >
                      <X className={`w-5 h-5 ${textSecondary}`} />
                    </button>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* Star Rating */}
                    <div className="text-center">
                      <p className={`text-sm ${textSecondary} mb-2`}>Puanınız</p>
                      <div className="flex justify-center">
                        <StarRating
                          rating={reviewForm.rating}
                          onChange={(rating) => setReviewForm({ ...reviewForm, rating })}
                          size="lg"
                          interactive
                        />
                      </div>
                      <p className={`text-xs ${textSecondary} mt-1`}>
                        {reviewForm.rating === 5 && 'Mükemmel!'}
                        {reviewForm.rating === 4 && 'Çok İyi'}
                        {reviewForm.rating === 3 && 'Orta'}
                        {reviewForm.rating === 2 && 'Kötü'}
                        {reviewForm.rating === 1 && 'Çok Kötü'}
                      </p>
                    </div>

                    {/* Comment */}
                    <div>
                      <label className={`text-sm ${textSecondary} mb-1 block`}>Yorumunuz</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="Deneyiminizi paylaşın..."
                        rows={4}
                        className={`w-full px-4 py-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-xl text-sm ${textPrimary} resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                      />
                    </div>

                    {/* Submit */}
                    <button
                      onClick={handleSubmitReview}
                      disabled={submittingReview || !reviewForm.comment.trim()}
                      className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submittingReview ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {editingReview ? 'Güncelle' : 'Gönder'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews List */}
            {reviewsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className={`w-8 h-8 animate-spin ${textSecondary}`} />
              </div>
            ) : reviews.length > 0 ? (
              <div className="space-y-3">
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            ) : (
              <div className={`${bgCard} rounded-2xl p-6 text-center`}>
                <MessageSquare className={`w-12 h-12 mx-auto mb-3 ${textSecondary}`} />
                <h3 className={`font-semibold ${textPrimary} mb-1`}>Henüz yorum yok</h3>
                <p className={`text-sm ${textSecondary} mb-4`}>Bu işletme hakkında ilk yorumu siz yapın</p>
                {user && (
                  <button
                    onClick={() => setShowReviewForm(true)}
                    className="px-6 py-2 bg-emerald-500 text-white rounded-xl font-medium"
                  >
                    Yorum Yaz
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="space-y-4">
            {data.gallery.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {data.gallery.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(img)}
                    className="aspect-square rounded-xl overflow-hidden"
                  >
                    <img src={img} alt={`Galeri ${index + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform" />
                  </button>
                ))}
              </div>
            ) : (
              <div className={`${bgCard} rounded-2xl p-6 text-center`}>
                <Camera className={`w-12 h-12 mx-auto mb-3 ${textSecondary}`} />
                <h3 className={`font-semibold ${textPrimary} mb-1`}>Galeri boş</h3>
                <p className={`text-sm ${textSecondary}`}>Bu işletmeye ait fotoğraf bulunmuyor</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
            <X className="w-6 h-6 text-white" />
          </button>
          <img src={selectedImage} alt="Galeri" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* Claim Business Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className={`${bgCard} rounded-2xl w-full max-w-md`}>
            <div className={`p-4 border-b ${borderColor} flex items-center justify-between`}>
              <h3 className={`font-semibold ${textPrimary}`}>İşletme Talep Et</h3>
              <button
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimMessage('');
                }}
                className={`w-8 h-8 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} flex items-center justify-center`}
              >
                <X className={`w-5 h-5 ${textSecondary}`} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className={`p-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-xl`}>
                <p className={`text-sm ${textPrimary} font-medium`}>{data.name}</p>
                <p className={`text-xs ${textSecondary}`}>{data.address}</p>
              </div>

              <div>
                <label className={`text-sm ${textSecondary} mb-1 block`}>
                  Neden bu işletmenin sahibi olduğunuzu açıklayın *
                </label>
                <textarea
                  value={claimMessage}
                  onChange={(e) => setClaimMessage(e.target.value)}
                  placeholder="Örn: Bu restoranın sahibiyim, vergi numaramı paylaşabilirim..."
                  rows={4}
                  className={`w-full px-4 py-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-xl text-sm ${textPrimary} resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                />
              </div>

              <p className={`text-xs ${textSecondary}`}>
                Talebiniz admin tarafından incelenecek ve onaylandığında işletmeyi yönetmeye başlayabilirsiniz.
              </p>

              <button
                onClick={handleSubmitClaim}
                disabled={submittingClaim || !claimMessage.trim()}
                className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingClaim ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Talep Gönder
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BusinessDetailPage;
